import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n';
import { SlideOver, SlideOverFooter } from '../../../components/Shared/SlideOver';
import { FormField } from '../../../components/Shared/FormField';
import { TextArea } from '../../../components/Shared/TextArea';
import { SnippetPreviewPanel } from '../../../components/Shared/SnippetPreviewPanel';
import { useTextSnippetStore } from '../../../store/useTextSnippetStore';
import type {
  CreateTextSnippetRequest,
  TextSnippetCategory,
  TextSnippetDto,
  UpdateTextSnippetRequest,
} from '../../../types/textSnippets';
import {
  INVOICE_SNIPPET_CATEGORIES,
  OFFER_SNIPPET_CATEGORIES,
} from '../../../types/textSnippets';
import { renderSample } from '../../../utils/snippetSample';
import TokenHelper from './TokenHelper';

const MAX_NAME = 200;
const MAX_BODY = 8000;

interface TextSnippetFormProps {
  isOpen: boolean;
  onClose: () => void;
  snippet?: TextSnippetDto | null;
  defaultCategory: TextSnippetCategory;
}

interface FormErrors {
  name?: string;
  body?: string;
  sortOrder?: string;
}

export default function TextSnippetForm({
  isOpen,
  onClose,
  snippet,
  defaultCategory,
}: TextSnippetFormProps) {
  const { t } = useTranslation(['textSnippets', 'common']);
  const isEdit = !!snippet;

  const createSnippet = useTextSnippetStore((s) => s.createSnippet);
  const updateSnippet = useTextSnippetStore((s) => s.updateSnippet);
  const saving = useTextSnippetStore((s) => s.saving);

  const [category, setCategory] = useState<TextSnippetCategory>(defaultCategory);
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState<number>(0);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState(false);

  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (snippet) {
      setCategory(snippet.category);
      setName(snippet.name);
      setBody(snippet.body);
      setIsDefault(snippet.isDefault);
      setIsActive(snippet.isActive);
      setSortOrder(snippet.sortOrder);
    } else {
      setCategory(defaultCategory);
      setName('');
      setBody('');
      setIsDefault(false);
      setIsActive(true);
      setSortOrder(0);
    }
    setErrors({});
    setTouched(false);
  }, [isOpen, snippet, defaultCategory]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!name.trim()) next.name = t('textSnippets:validation.nameRequired');
    else if (name.length > MAX_NAME) next.name = t('textSnippets:validation.nameTooLong');
    if (!body.trim()) next.body = t('textSnippets:validation.bodyRequired');
    else if (body.length > MAX_BODY) next.body = t('textSnippets:validation.bodyTooLong');
    if (!Number.isFinite(sortOrder) || sortOrder < 0)
      next.sortOrder = t('textSnippets:validation.sortOrderInvalid');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const markDirty = () => setTouched(true);

  const handleInsertToken = (token: string) => {
    const el = bodyRef.current;
    if (!el) {
      setBody((prev) => prev + token);
      markDirty();
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    markDirty();
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      if (isEdit && snippet) {
        const data: UpdateTextSnippetRequest = {
          name: name.trim(),
          body,
          isActive,
          sortOrder,
        };
        await updateSnippet(snippet.id, data);
      } else {
        const data: CreateTextSnippetRequest = {
          category,
          name: name.trim(),
          body,
          isDefault,
          sortOrder,
        };
        const created = await createSnippet(data);
        if (!created) return;
      }
      onClose();
    } catch {
      // toast already fired by store
    }
  };

  const footer = (
    <SlideOverFooter
      onCancel={onClose}
      onConfirm={handleSubmit}
      confirmText={t('common:actions.save')}
      cancelText={t('common:actions.cancel')}
      loading={saving}
    />
  );

  const title = isEdit
    ? t('textSnippets:actions.edit') + ': ' + (snippet?.name ?? '')
    : t('textSnippets:actions.create');

  const categoryGroups: { documentType: 'offer' | 'invoice'; categories: readonly TextSnippetCategory[] }[] = [
    { documentType: 'offer', categories: OFFER_SNIPPET_CATEGORIES },
    { documentType: 'invoice', categories: INVOICE_SNIPPET_CATEGORIES },
  ];

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="xl"
      hasUnsavedChanges={touched && !saving}
      footer={footer}
    >
      <div className="space-y-5">
        <FormField
          label={t('textSnippets:fields.category')}
          name="category"
          required
        >
          <select
            value={category}
            // why: UpdateTextSnippetRequest carries no category — it is fixed once created.
            disabled={isEdit}
            onChange={(e) => {
              setCategory(e.target.value as TextSnippetCategory);
              markDirty();
            }}
            className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50 disabled:bg-slate-50 disabled:cursor-not-allowed"
          >
            {categoryGroups.map((group) => (
              <optgroup
                key={group.documentType}
                label={t(`textSnippets:documentTypes.${group.documentType}`)}
              >
                {group.categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {t(`textSnippets:categories.${cat}`)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </FormField>

        <FormField
          label={t('textSnippets:fields.name')}
          name="name"
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            markDirty();
          }}
          maxLength={MAX_NAME}
          error={errors.name}
          helperText={`${name.length} / ${MAX_NAME}`}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-3">
            <TextArea
              ref={bodyRef}
              label={t('textSnippets:fields.body')}
              name="body"
              required
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                markDirty();
              }}
              maxLength={MAX_BODY}
              rows={12}
              error={errors.body}
              helperText={`${body.length} / ${MAX_BODY}`}
            />
            <SnippetPreviewPanel
              text={renderSample(body, t, category)}
              hint={t('textSnippets:preview.sampleHint')}
            />
          </div>
          <div className="md:col-span-1">
            <TokenHelper onInsert={handleInsertToken} category={category} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label={t('textSnippets:fields.sortOrder')}
            name="sortOrder"
            type="number"
            min={0}
            value={sortOrder}
            onChange={(e) => {
              setSortOrder(parseInt(e.target.value, 10) || 0);
              markDirty();
            }}
            error={errors.sortOrder}
          />

          {isEdit ? (
            <label className="flex items-center gap-2 mt-7 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => {
                  setIsActive(e.target.checked);
                  markDirty();
                }}
                className="h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
              />
              <span className="text-[13px] font-medium text-slate-700">
                {t('textSnippets:fields.isActive')}
              </span>
            </label>
          ) : (
            <label className="flex items-center gap-2 mt-7 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => {
                  setIsDefault(e.target.checked);
                  markDirty();
                }}
                className="h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
              />
              <span className="text-[13px] font-medium text-slate-700">
                {t('textSnippets:fields.isDefault')}
              </span>
            </label>
          )}
        </div>

        {isEdit && (
          <p className="text-[12px] text-slate-500">
            {t('textSnippets:messages.defaultLockedOnEdit')}
          </p>
        )}
      </div>
    </SlideOver>
  );
}
